import type { ApolloError, ApolloQueryResult } from '@apollo/client';
import type { ResultOf } from '@graphql-typed-document-node/core';
import type { FragmentType } from 'src/gql';
import React, { useState } from 'react';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { TrackedButton } from 'src/components/tracked';
import { graphql, unmaskFragment } from 'src/gql';

import { MarketingRequestDialog } from './marketing-request-dialog';
import { MarketingRequestRow } from './marketing-request-row';

export const OPEN_STATUSES = new Set(['Requested', 'In Progress', 'Compliance Review', 'In Design', 'On Hold']);

export const COMPLETED_STATUSES = new Set(['Completed', 'Compliance Approved']);

export const WrikeMarketingRequestListFragment = graphql(/* GraphQL */ `
  fragment WrikeMarketingRequestList on WrikeMarketingRequests {
    requests {
      title
      issueId
      status
      assigneeFirstName
      createdAt
      dueDate
      advisorLabel
      advisorGroupLabel
    }
    userIsInAdvisorGroup
  }
`);

export const MARKETING_REQUEST_CNT_MAX_VIEW = 4;

export type MarketingRequest = NonNullable<
  NonNullable<ResultOf<typeof WrikeMarketingRequestListFragment>>['requests']
>[number];

export function MarketingRequestList({
  wrikeQueryFrag,
  loading,
  error,
  refetchWrikeMarketingRequests: _refetchWrikeMarketingRequests,
}: {
  readonly wrikeQueryFrag?: FragmentType<typeof WrikeMarketingRequestListFragment> | null;
  readonly loading: boolean;
  readonly error?: ApolloError;
  readonly refetchWrikeMarketingRequests: () => Promise<ApolloQueryResult<unknown>>;
}) {
  const wrikeData = wrikeQueryFrag ? unmaskFragment(WrikeMarketingRequestListFragment, wrikeQueryFrag) : null;

  const [dialogOpen, setDialogOpen] = useState(false);

  const allRequests = wrikeData?.requests || [];
  const openRequests = allRequests.filter((r) => OPEN_STATUSES.has(r.status));
  const completedRequests = allRequests.filter((r) => COMPLETED_STATUSES.has(r.status));
  const displayRequests = openRequests.slice(0, MARKETING_REQUEST_CNT_MAX_VIEW);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="divide-y divide-border-light">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between px-4 py-3">
              <div className="flex flex-col gap-2 flex-1">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/3" />
              </div>
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
          ))}
        </div>
      );
    }

    if (error) {
      return <p className="px-4 py-4 text-sm text-destructive">Error loading requests</p>;
    }

    if (displayRequests.length === 0) {
      return (
        <div className="px-4 py-6 flex items-center justify-center">
          <p className="text-sm text-secondary">No open requests</p>
        </div>
      );
    }

    return (
      <div className="divide-y divide-border-light">
        {displayRequests.map((request) => (
          <MarketingRequestRow key={request.issueId} request={request} />
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col rounded-md border border-light overflow-hidden">
        <div className="flex items-center justify-between bg-stone-50 dark:bg-card px-4 py-3">
          <div>
            <h4 className="text-sm font-semibold">Active requests</h4>
            {!loading && openRequests.length > 0 && (
              <p className="text-xs text-secondary">{openRequests.length} open</p>
            )}
          </div>
          <TrackedButton
            elementName="view-all-marketing-requests-button"
            variant="ghost"
            size="sm"
            onClick={() => setDialogOpen(true)}
          >
            View completed
          </TrackedButton>
        </div>
        <div className="flex flex-col">{renderContent()}</div>
      </div>

      <MarketingRequestDialog open={dialogOpen} onClose={() => setDialogOpen(false)} requests={completedRequests} />
    </>
  );
}
